/**
 * Human Resources — internationalisation.
 *
 * A thin adapter over the estate's shared engine in `@isg/ui-kit`, modelled on
 * isg-induction's. Pages and components import `t` / `getLocale` from here so
 * that there is one place that owns this app's catalogue.
 *
 * ⚠️ This app's catalogue is NESTED (`{"nav": {"documents": …}}`), like
 * isg-intranet's. Keep it that way; the shared lookup walks the dotted path.
 *
 * Catalogues: `./en-GB.json` is the base and must be complete — a missing key
 * renders as the key itself. `./locales/{de,cs,en-US}.json` are produced by
 * ui-kit's translate-catalogue.py; a key missing there falls back to English.
 */
import {
  registerAppMessages, startAppI18n,
  t, getLocale, setLocale, onLocaleChange, getAvailableLocales,
} from '@isg/ui-kit';

import enGB from './en-GB.json';

// ⚠️ AT MODULE SCOPE — any t() evaluated during the import graph would
// otherwise render its own key, in every language including English.
registerAppMessages('en-GB', enGB);

export { t, getLocale, setLocale, onLocaleChange, getAvailableLocales };

/**
 * Boot i18n for this app, before the shell or navigation renders.
 *
 * @param {object|null} user the signed-in user (`user.locale` is the stored
 *        estate-wide preference), or null when signed out.
 * @returns {Promise<string>} the resolved locale.
 */
export function initI18n(user = null) {
  return startAppI18n({
    base: enGB,
    // Literal template so Vite can glob ./locales/*.json at build time.
    load: (locale) => import(`./locales/${locale}.json`),
    user,
  });
}
